<template>
  <div>
    <template v-if="!isListScreenType">
      <div class="form-title">
        {{ t("Share order title") }}
      </div>
      <div class="pa-2"/>
    </template>

    <template v-if="isLoading">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
    </template>

    <div
      v-else
      class="submitted-content-share-order"
    >
      <div class="intro-container">
        <div v-html="t('Select whom to share order' + translationKeySuffix)"/>
        <div class="d-flex mt-5 email-address">
          <InputText
            v-model="emailAdressToAdd"
            class="w-100 col-12 mr-3 pa-0"
            :label="t('Email address')"
            :rules="emailRules"
            required
          />
          <Button
            :disabled="!validEmail"
            @click="addEmail"
          >
            {{ t("Add") }}
          </Button>
        </div>
      </div>

      <div class="sub-title mb-5">
        {{ t("Quick Selection List") }}
      </div>

      <v-data-table
        :headers="headers"
        :items="allSharedUsers"
        :items-per-page="-1"
        :no-data-text="t('No data available')"
        mobile-breakpoint="-1"
        hide-default-footer
        class="share-order-table"
      >
        <template #item.select="{ item }">
          <v-checkbox
            v-model="selectedUser"
            :disabled="item.doctorId === authUserId"
            :value="item.doctorEmail || item.email"
          />
        </template>
        <template #item.salutation="{ item }">
          {{ item.salutation || item.doctorSalutation }}
        </template>
        <template #item.title="{ item }">
          {{ item.title || item.doctorTitle }}
        </template>
        <template #item.firstName="{ item }">
          {{ item.firstName || item.doctorFirstName }}
        </template>
        <template #item.lastName="{ item }">
          {{ item.lastName || item.doctorLastName }}
        </template>
        <template #item.email="{ item }">
          {{ item.email || item.doctorEmail }}
        </template>
      </v-data-table>

      <div v-if="isListScreenType">
        <Button
          class="mt-6"
          @click="closeSubmitOrderShareDialog('finish')"
        >
          {{ t("Confirm consent & share order") }}
        </Button>
      </div>
      <template>
        <SubmitOrderShareDialog
          v-model="showDialog"
          :title="
            t('Confirm Share Order - Order {orderId}', {
              orderId: orderId.toString(),
            })
          "
          :allSharedUsers="allSharedUsers"
          :selectedUser="selectedUser"
          :width="'800'"
          @cancel="closeSubmitOrderShareDialog"
          @closePopup="closeSubmitOrderShareDialog('finish')"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed,  inject, onMounted, ref, watch} from "vue";
import InputText from "@/components/input-components/InputText.vue";
import Button from "@/components/input-components/Button.vue";
import SubmitOrderShareDialog from "@/components/dialogs/SubmitOrderShareDialog.vue";
import {ProfileService} from "@/services/profile.service";
import {OrderUsersService} from "@/services/orderusers.service";
import NotificationMessageService from "@/services/notificationmessage.service";
import {EMAIL_REG_EXP, SHARE_ORDER_SCREEN_TYPE} from "@/utils/constants";
import {useI18n} from "vue-i18n";
import {useOrdersStore} from "@/stores/orders";
import {useProfileStore} from "@/stores/profile";
import {useAuth} from "@/composables/useAuth";
import {authKey} from "@/plugins/authentication";
import {OrderUser} from "@/entities/order-user";

const props = withDefaults(defineProps<
  {
    orderId: number;
    screenType: string;
    modelValue?: boolean;
    orderUsers?: OrderUser[];
  }>(), {
  modelValue: false,
  orderUsers: () => [],
});

const emit = defineEmits<{
  (e: 'closeSubmitOrderShareDialog', args?: any): void;
  (e: 'hasSelectedUsers', hasUsers: boolean): void;
  (e: 'update:modelValue', val: boolean): void;
}>();

const { t } = useI18n()
const { user } = useAuth()

const auth = inject(authKey)!;
const authUserId = computed(() => auth.value?.sub);
const ordersStore = useOrdersStore();
const profileStore = useProfileStore();
const profileService = new ProfileService();
const orderUserService = new OrderUsersService();

const allSharedUsers = ref<any[]>([]);
const selectedUser = ref<string[]>([]);
const emailAdressToAdd = ref("");
const showDialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})
const isLoading = ref(false);
const initialSelectedUsers = ref<string[]>([]);

const validEmail = computed(() => EMAIL_REG_EXP.test(emailAdressToAdd.value));
const isListScreenType = computed(() => props.screenType === SHARE_ORDER_SCREEN_TYPE.list);
const translationKeySuffix = computed(() => (isListScreenType.value ? " (list)" : ""));

const headers = computed(() => [
  {title: "", value: "select", sortable: false},
  {title: t("Salutation"), value: "salutation", sortable: false},
  {title: t("Title"), value: "title", sortable: false},
  {title: t("First name"), value: "firstName", sortable: false},
  {title: t("Last name"), value: "lastName", sortable: false},
  {title: t("Email"), value: "email", sortable: false},
]);

const emailRules = [(v: string) => !v || EMAIL_REG_EXP.test(v) || t("This field must contain a valid email")];

watch(selectedUser, (val) => emit("hasSelectedUsers", val.length > 0), {immediate: true});

async function getSharedUsersProfile() {
  isLoading.value = true;
  selectedUser.value = [];

  const userEmail = user.value.email;
  const sharedUsersProfile = await profileService.getSharedUsers();

  let sharedUsersOrder: OrderUser[];
  if (props.orderUsers && props.orderUsers.length > 0) {
    sharedUsersOrder = props.orderUsers;
  } else {
    sharedUsersOrder = await orderUserService.getOrderUsers(props.orderId);
  }

  const filteredProfiles = sharedUsersProfile.filter(
    (u: any) =>
      !sharedUsersOrder.some((o) => o.doctorEmail === u.email) &&
      u.email !== userEmail
  );

  const filteredOrderUsers = sharedUsersOrder.filter(
    (o) => o.doctorEmail !== userEmail
  );

  allSharedUsers.value = [...filteredProfiles, ...filteredOrderUsers];
  selectedUser.value = filteredOrderUsers.map((o) => o.doctorEmail);
  initialSelectedUsers.value = [...selectedUser.value];

  isLoading.value = false;
}

function addEmail() {
  if (
    !allSharedUsers.value.some((e) => e.email === emailAdressToAdd.value || e.doctorEmail === emailAdressToAdd.value) &&
    EMAIL_REG_EXP.test(emailAdressToAdd.value)
  ) {
    allSharedUsers.value.unshift({email: emailAdressToAdd.value});
    selectedUser.value.push(emailAdressToAdd.value);
    emailAdressToAdd.value = "";
    NotificationMessageService.showSuccess(t("E-Mail has been added.") as string);
  } else {
    NotificationMessageService.showError(t("E-Mail already in use or Input field is empty.") as string);
  }
}

async function closeSubmitOrderShareDialog(args: any = null) {
  if (args === "finish") {
    const removeUsers = initialSelectedUsers.value.filter((iu) => !selectedUser.value.includes(iu));
    const addUsers = selectedUser.value.filter((su) => !initialSelectedUsers.value.includes(su));

    let error = false;

    for (const email of addUsers) {
      try {
        await orderUserService.postOrderUsers(props.orderId, email);
      } catch {
        error = true;
      }
    }

    for (const email of removeUsers) {
      const userToRemove = allSharedUsers.value.find((u) => u.doctorEmail === email);
      if (userToRemove) {
        try {
          await orderUserService.deleteOrderUser(userToRemove.orderUserId);
        } catch {
          error = true;
        }
      }
    }

    if (error) {
      NotificationMessageService.showError(
        t('An error occurred while sharing the order. Please check under "Share order".') as string
      );
    } else if (isListScreenType.value) {
      NotificationMessageService.showSuccess(t("Consent confirmed and order successfully shared") as string);
    }
  }

  showDialog.value = false;
  emit("closeSubmitOrderShareDialog", args);
}

onMounted(getSharedUsersProfile);
</script>
<style lang="scss">
.submitted-content-share-order {
  text-align: left;

  .v-label {
    opacity: var(--v-radio-label-opacity) !important;
  }

  .v-selection-control--dirty {
    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }

  .intro-container {
    max-width: 100%;
  }

  :deep(.email-address) {
    width: 100%;
  }

  .sub-title {
    margin: 0.625rem 0;
    font-weight: bold;
  }

  .share-order-table {
    .v-input--checkbox .v-field__input {
      margin-bottom: 0;
    }
  }
}
</style>
